/* eslint-disable no-nested-ternary, no-param-reassign */

import React, { memo, useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useTranslate } from 'react-admin';
import { Box, IconButton, Tooltip, makeStyles } from '@material-ui/core';
import { Cancel, DeleteRounded, Edit, FileCopy, Save } from '@material-ui/icons';
import clsx from 'clsx';
import { INVOICE_SERVICE } from '../../../../../constant/invoice';
import AntTable from '../../../../../base/components/antd/AntdTable';
import CurrencySelect from './CurrencySelect';
import { CurrencyDisplayModeEnum } from '../../../../../constant';
import CurrencyModeSelect from './CurrencyModeSelect';
import MonthlyRoyaltyInput from './MonthlyRoyaltyInput';
import MktRoyaltyInput from './MktRoyaltyInput';
import { useDefaultRoyalty } from '../../utils';
import { useHasCreateInvoicePermission, useHasReadInvoicePermission } from '../../../../group/components/utils';

const EditableCell = ({ editing, dataIndex, children, defaultMktRoyalty, defaultMonthlyRoyalty, ...restProps }) => (
  <td {...restProps}>
    {editing ? (
      dataIndex === 'monthlyRoyalty' ? (
        <MonthlyRoyaltyInput defaultRoyalty={defaultMonthlyRoyalty} />
      ) : dataIndex === 'mktRoyalty' ? (
        <MktRoyaltyInput defaultRoyalty={defaultMktRoyalty} />
      ) : dataIndex === 'mode' ? (
        <CurrencyModeSelect />
      ) : dataIndex === 'id' ? (
        <CurrencySelect />
      ) : (
        children
      )
    ) : (
      children
    )}
  </td>
);

const EditableCurrenciesTable = ({ currencies, onChange }) => {
  const classes = useStyles();
  const t = useTranslate();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const hasCreateInvoicePermission = useHasCreateInvoicePermission();
  const hasReadInvoicePermission = useHasReadInvoicePermission();
  const { defaultMonthlyRoyalty, defaultMktRoyalty } = useDefaultRoyalty();
  const isEditing = useCallback((record) => record.id === editingKey, [editingKey]);

  const cancel = useCallback(() => {
    setEditingKey('');
  }, []);

  const columns = useMemo(() => {
    const edit = (record) => {
      form.setFieldsValue({
        monthlyRoyalty: record.monthlyRoyalty || defaultMonthlyRoyalty,
        mktRoyalty: record.mktRoyalty || defaultMktRoyalty,
        currency: {
          id: record.id,
          name: record.name,
          isThinEnabled: record.isThinEnabled,
        },
        mode: record.mode || CurrencyDisplayModeEnum.NORMAL,
      });
      setEditingKey(record.id);
    };

    const save = async (id) => {
      try {
        const changes = await form.validateFields();
        onChange('update', {
          id,
          ...changes,
          mktRoyalty: changes.mktRoyalty === defaultMktRoyalty ? undefined : changes.mktRoyalty,
          monthlyRoyalty: changes.monthlyRoyalty === defaultMonthlyRoyalty ? undefined : changes.monthlyRoyalty,
        });
        setEditingKey('');
      } catch (errInfo) {
        console.log(errInfo);
      }
    };

    const remove = (record) => {
      onChange('delete', record);
    };

    return [
      {
        title: t('resources.currency.name'),
        key: 'id',
        dataIndex: 'id',
        editable: true,
        render: (_, record) => record.name,
      },
      ...(hasReadInvoicePermission
        ? [
            {
              title: t(`common.invoiceServiceRoyalty.${INVOICE_SERVICE.MONTHLY_GGR}`),
              dataIndex: 'monthlyRoyalty',
              key: 'monthlyRoyalty',
              width: '20%',
              editable: hasCreateInvoicePermission,
              render: (monthlyRoyalty) => {
                const rate = monthlyRoyalty || defaultMonthlyRoyalty;
                const isDefault = rate === defaultMonthlyRoyalty;
                return rate ? (
                  <>
                    {`${rate}%`}{' '}
                    <span className={isDefault ? classes.defaultText : classes.customText}>
                      {`(${isDefault ? t('common.text.default') : t('common.text.custom')})`}
                    </span>
                  </>
                ) : (
                  '-'
                );
              },
            },
            {
              title: t(`common.invoiceServiceRoyalty.${INVOICE_SERVICE.MKT_FREE_SPIN}`),
              dataIndex: 'mktRoyalty',
              key: 'mktRoyalty',
              width: '20%',
              editable: hasCreateInvoicePermission,
              render: (mktRoyalty) => {
                const rate = mktRoyalty || defaultMktRoyalty;
                const isDefault = rate === defaultMktRoyalty;
                return rate ? (
                  <>
                    {`${rate}%`}{' '}
                    <span className={isDefault ? classes.defaultText : classes.customText}>
                      {`(${isDefault ? t('common.text.default') : t('common.text.custom')})`}
                    </span>
                  </>
                ) : (
                  '-'
                );
              },
            },
          ]
        : []),
      {
        title: t('resources.brand.fields.currencyMode'),
        dataIndex: 'mode',
        key: 'mode',
        width: '20%',
        verticalAlign: 'center',
        editable: true,
        render: (mode) =>
          mode === CurrencyDisplayModeEnum.THIN
            ? t('resources.brand.text.prefixedDisplayed')
            : t('resources.brand.text.normalDisplayed'),
      },
      {
        title: t('resources.campaign.fields.action'),
        key: 'action',
        width: '80px',
        fixed: 'right',
        render: (_, record) => {
          const editable = isEditing(record);
          return (
            <Box
              display="flex"
              justifyContent="center"
              sx={{
                gap: 4,
              }}
            >
              {editable ? (
                <>
                  <Box
                    component={IconButton}
                    size="small"
                    color="primary"
                    sx={{
                      width: 'fit-content',
                    }}
                    onClick={() => save(record.id)}
                  >
                    <Save fontSize="small" />
                  </Box>
                  <Box
                    component={IconButton}
                    size="small"
                    color="primary"
                    sx={{
                      width: 'fit-content',
                    }}
                    onClick={cancel}
                  >
                    <Cancel fontSize="small" />
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    component={IconButton}
                    size="small"
                    color="primary"
                    sx={{
                      width: 'fit-content',
                    }}
                    disabled={editingKey !== ''}
                    onClick={() => edit(record)}
                  >
                    <Edit fontSize="small" />
                  </Box>

                  <Tooltip title={t('resources.brand.applyToAll')}>
                    <Box
                      component={IconButton}
                      size="small"
                      color="primary"
                      sx={{
                        width: 'fit-content',
                        '& svg': {
                          width: 17,
                        },
                      }}
                      onClick={() => {
                        cancel();
                        onChange(
                          'replace',
                          currencies.map((currency) => ({
                            ...currency,
                            mode:
                              record.mode === CurrencyDisplayModeEnum.THIN && !record.isThinEnabled
                                ? CurrencyDisplayModeEnum.NORMAL
                                : record.mode,
                            mktRoyalty: record.mktRoyalty,
                            monthlyRoyalty: record.monthlyRoyalty,
                          })),
                        );
                      }}
                    >
                      <FileCopy />
                    </Box>
                  </Tooltip>
                  <Box
                    component={IconButton}
                    size="small"
                    className={classes.deleteButton}
                    sx={{
                      width: 'fit-content',
                    }}
                    disabled={editingKey !== ''}
                    onClick={() => remove(record)}
                  >
                    <DeleteRounded fontSize="small" />
                  </Box>
                </>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    isEditing,
    cancel,
    editingKey,
    onChange,
    currencies,
    defaultMktRoyalty,
    defaultMonthlyRoyalty,
    classes,
    hasCreateInvoicePermission,
    hasReadInvoicePermission,
  ]);

  const mergedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (!col.editable) {
          return col;
        }
        return {
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
            title: col.title,
            key: col.key,
            editing: isEditing(record),
            defaultMktRoyalty,
            defaultMonthlyRoyalty,
          }),
        };
      }),
    [columns, isEditing, defaultMktRoyalty, defaultMonthlyRoyalty],
  );

  return (
    <Form form={form} component={false}>
      <AntTable
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        sticky
        dataSource={currencies}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={{
          onChange: cancel,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          className: classes.pagination,
        }}
        scroll={{
          x: 'max-content',
        }}
        className={clsx(classes.root, {
          [classes.empty]: !currencies?.length,
        })}
        wrapperProps={{
          className: classes.tableWrapper,
        }}
        rowKey="id"
      />
    </Form>
  );
};

export default memo(EditableCurrenciesTable);

const useStyles = makeStyles((theme) => ({
  tableWrapper: {
    padding: '0px 4px',
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  root: {
    maxHeight: 'calc(100vh - 450px)',
    overflowY: 'scroll',
  },
  pagination: {
    position: 'sticky',
    bottom: 0,
    background: 'white',
    margin: '0 !important',
    padding: '12px 0',
    alignItems: 'center',
    zIndex: 2,
  },
  empty: {
    '&.bo-table.ant-table-wrapper td.ant-table-cell': {
      borderBottom: 'none',
    },
  },
  customText: {
    fontSize: 12,
    fontStyle: 'italic',
    color: theme.palette.primary.main,
  },
  defaultText: {
    fontSize: 12,
    fontStyle: 'italic',
    color: theme.palette.grey[600],
  },
}));
